import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  Stack,
  Chip,
  IconButton,
} from '@mui/material';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';

export interface FontSizeOption {
  value: number;
  label: string;
  description: string;
}

export const FONT_SIZE_OPTIONS: FontSizeOption[] = [
  { value: 0.6, label: 'Çok Küçük', description: 'Çok fazla masa sığdırmak için kompakt görünüm' },
  { value: 0.8, label: 'Küçük', description: 'Biraz daha kompakt, daha fazla masa' },
  { value: 1.0, label: 'Orta', description: 'Varsayılan boyut, dengeli görünüm' },
  { value: 1.25, label: 'Büyük', description: 'Uzaktan daha okunaklı görünüm' },
  { value: 1.5, label: 'Çok Büyük', description: 'En belirgin ve okunaklı görünüm' },
];

export const DEFAULT_TABLE_NAME_FONT_SCALE = 1.0;

interface Props {
  open: boolean;
  onClose: () => void;
  currentScale: number;
  onApply: (scale: number) => void;
}

const TableNameFontSizeModal: React.FC<Props> = ({ open, onClose, currentScale, onApply }) => {
  const [selectedScale, setSelectedScale] = useState<number>(currentScale);

  useEffect(() => {
    if (open) setSelectedScale(currentScale);
  }, [open, currentScale]);

  const handleApply = () => {
    onApply(selectedScale);
    onClose();
  };

  const handleChange = (_: React.MouseEvent<HTMLElement>, v: number | null) => {
    if (v !== null) setSelectedScale(v);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: '#fff3e0',
          color: '#e65100',
          borderBottom: '2px solid #ff9800',
          py: 1.5,
        }}
      >
        <TextFieldsIcon />
        <Typography variant="h6" fontWeight="bold" sx={{ flex: 1 }}>
          Masa İsmi Yazı Boyutu
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: '#e65100' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Masa kartlarında görünen masa isminin boyutunu seçin. Önizleme alanında seçtiğiniz boyutu anında görebilirsiniz.
        </Typography>

        {/* Önizleme */}
        <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          ÖNİZLEME
        </Typography>
        <Card
          sx={{
            bgcolor: '#1976d2',
            p: 2,
            mb: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 140,
            border: '2px dashed rgba(255,255,255,0.4)',
            borderRadius: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: `${2 * selectedScale}rem`,
              fontWeight: 'bold',
              color: 'white',
              lineHeight: 1.2,
              transition: 'font-size 0.2s ease',
            }}
          >
            5
          </Typography>
          <TableRestaurantIcon sx={{ fontSize: 20, color: 'white', mt: 0.5 }} />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.5, fontSize: '0.65rem' }}>
            Örnek masa kartı — "Masa 5"
          </Typography>
        </Card>

        {/* Seçenekler */}
        <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          BOYUT SEÇENEKLERİ
        </Typography>
        <ToggleButtonGroup
          value={selectedScale}
          exclusive
          onChange={handleChange}
          orientation="vertical"
          fullWidth
          sx={{ gap: 1 }}
        >
          {FONT_SIZE_OPTIONS.map((opt) => (
            <ToggleButton
              key={opt.value}
              value={opt.value}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                border: '1px solid !important',
                borderColor: 'divider !important',
                borderRadius: '8px !important',
                p: 1.25,
                '&.Mui-selected': {
                  borderColor: '#e65100 !important',
                  bgcolor: '#fff3e0 !important',
                  '&:hover': { bgcolor: '#ffe0b2 !important' },
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                <Box
                  sx={{
                    minWidth: 56,
                    height: 56,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(25, 118, 210, 0.08)',
                    borderRadius: 1.5,
                  }}
                >
                  <Typography sx={{ fontSize: `${1.4 * opt.value}rem`, fontWeight: 'bold', color: '#1976d2', lineHeight: 1 }}>
                    Aa
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography fontWeight="bold" sx={{ color: 'text.primary' }}>
                      {opt.label}
                    </Typography>
                    {opt.value === DEFAULT_TABLE_NAME_FONT_SCALE && (
                      <Chip label="Varsayılan" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                    {selectedScale === opt.value && (
                      <Chip
                        icon={<CheckIcon sx={{ fontSize: '0.85rem !important' }} />}
                        label="Seçili"
                        size="small"
                        color="warning"
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {opt.description}
                  </Typography>
                </Box>
              </Stack>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 2 }}>
        <Button onClick={onClose} color="inherit" startIcon={<CloseIcon />}>
          İptal
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          color="warning"
          startIcon={<CheckIcon />}
          disabled={selectedScale === currentScale}
        >
          Uygula
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TableNameFontSizeModal;
